import "../../assets/ContentEditor.css";
import { useEffect, useRef, useState } from "react";
import { insideFieldContainer, insertFormField } from '../content-editor/field-container';
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Menu } from "primereact/menu"; 
import { MenuItem } from "primereact/menuitem";
import { useManagedModuleEditor } from "../../providers/managed-module-management-provider";
import { FormFieldDto } from "../../general/dtos";
import { LongTextTooltip } from '../long-text-tooltip';
import { MarkdownText } from '../markdown-text';
import { fieldDisplayNames } from '../content-viewer/field-display-names';
import { useFullscreenDialogParent } from '../../general/use-fullscreen-dialog-parent';

interface FormFieldWithUsage extends FormFieldDto {
  usedInEditor: boolean;
}

export function ManageFieldsPanel() {
  const overlayParent = useFullscreenDialogParent();
  const {
    fragments,
    selectedFragmentKey,
    openFieldEditor,
    addFormField,
    removeFormField,
    fieldsPanelVisible,
    closeFieldsPanel,
    editor,
  } = useManagedModuleEditor();

  const menuRef = useRef<Menu>(null);
  const [currentMenuItems, setCurrentMenuItems] = useState<MenuItem[]>([]);
  const [, setEditorRevision] = useState(0);
  useEffect(() => {
    const update = () => setEditorRevision(value => value + 1);
    editor?.on('transaction', update);
    return () => { editor?.off('transaction', update); };
  }, [editor]);

  if (!fieldsPanelVisible) return null;

  const fragment = fragments.find(f => f.key === selectedFragmentKey);
  if (!fragment) return null;

  const html = editor?.getHTML() ?? fragment.html ?? "";
  const names = fieldDisplayNames(fragment.formFields ?? [], html);

  const fields: FormFieldWithUsage[] = fragment?.formFields?.map(field => {
    let isUsed = false;
    
    if (field.type === 'quiz-list') {
      // For quiz pools, it's marked "Used" if ANY of its options are in the HTML
      // We search for field-key to be safe
      isUsed = html.includes(`field-key="${field.key}"`);
    } else {
      // Standard field check looking for the primary key attribute
      isUsed = html.includes(`key="${field.key}"`);
    }

    return {
      ...field,
      usedInEditor: isUsed,
    };
  });

  const handleDuplicate = (sourceField: FormFieldDto) => {
    if (!selectedFragmentKey) return;
    const duplicatedField: FormFieldDto = {
        ...sourceField,
        key: crypto.randomUUID(),
        label: sourceField.label.trim() ? `${sourceField.label} (Copy)` : '',
        options: sourceField.options?.map(opt => ({
            ...opt,
            key: `opt_${crypto.randomUUID().split('-')[0]}`
        }))
    };
    addFormField(selectedFragmentKey, duplicatedField);
    openFieldEditor(duplicatedField);
  };

  return (
    <div
      style={{
        width: "360px",
        background: "var(--surface-card)",
        borderLeft: "1px solid var(--surface-border)",
        padding: "0rem",
        display: "flex",
        flexDirection: "column",
        gap: "0rem",
        height: "100%"
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0 }}>Manage fields</h3>
        <Button icon="pi pi-times" text onClick={closeFieldsPanel} />
      </div>

      {fields?.length === 0 && <p style={{ opacity: 0.7 }}>No fields yet.</p>}

      <LongTextTooltip target=".field-description-tooltip" />

      <div style={{ display: "flex", flexDirection: "column", gap: "0rem", minWidth: '0', overflowY: 'auto' }}>
        {fields?.map(field => {
          const unusedOptions = field.type === 'quiz-list' 
            ? field.options?.filter(opt => !html.includes(`expected-key="${opt.key}"`)) ?? []
            : [];

          const isInsertDisabled = field.type === 'quiz-list' 
            ? unusedOptions.length === 0 || (!!editor && insideFieldContainer(editor))
            : field.usedInEditor;

          const handleInsertClick = (event: React.MouseEvent) => {
            if (field.type === 'quiz-list') {
              if (editor && insideFieldContainer(editor)) return;
              // Capture the target element now before the event is recycled
              const target = event.currentTarget;

              const items = unusedOptions.map(opt => ({
                label: opt.value,
                icon: 'pi pi-plus',
                command: () => {
                  editor?.commands.insertContent(
                    `<quiz-target field-key="${field.key}" expected-key="${opt.key}"></quiz-target> `
                  );
                }
              }));
              
              setCurrentMenuItems(items);
              
              // Toggle using the captured DOM element instead of the event object
              setTimeout(() => {
                menuRef.current?.toggle({ currentTarget: target, nativeEvent: event.nativeEvent } as any);
              }, 0);
            } else {
              if (editor) insertFormField(editor, field);
            }
          };

          return (
            <Card key={field.key} className="field-card" style={{ border: "1px solid var(--surface-border)", borderRadius: "8px" }} >
              <div className="field-row">
                <div className="field-description-tooltip" data-markdown-tooltip={field.tooltip ?? ''} data-pr-position="top" tabIndex={0}>
                  <div style={{ fontWeight: 600 }}>{field.label.trim() ? <MarkdownText text={field.label} inline /> : names[field.key]?.text}</div>
                  <div style={{ fontSize: "0.8rem", opacity: 0.7 }}>{field.type}</div>
                </div>
                <Tag
                  value={field.usedInEditor ? "Used" : "Unused"}
                  severity={field.usedInEditor ? "success" : "warning"}
                />
              </div>

              <div className="field-actions" style={{ display: "flex", gap: "0rem", marginTop: "0rem" }}>
                <Button
                  label="Insert"
                  icon="pi pi-arrow-left"
                  className="p-button-text p-button-sm"
                  size="small"
                  disabled={isInsertDisabled}
                  onClick={handleInsertClick}
                />
                <Button label="Edit" icon="pi pi-pencil" className="p-button-text p-button-sm" size="small" onClick={() => openFieldEditor(field)} />
                <Button label="Dupe" icon="pi pi-copy" className="p-button-text p-button-sm" size="small" onClick={() => handleDuplicate(field)} />
                <Button label="Delete" icon="pi pi-trash" className="p-button-text p-button-sm p-button-danger" severity="danger" size="small"
                  onClick={() => removeFormField(selectedFragmentKey!, field.key)}
                />
              </div>
            </Card>
          );
        })}
      </div>

      <Menu 
        ref={menuRef} 
        model={currentMenuItems}
        popup 
        id="quiz_options_menu" 
        appendTo={overlayParent}
        style={{ width: 'fit-content', minWidth: '200px' }}
      />
    </div>
  );
}
